import { AloftArea, AloftDateJSON, AloftPrice } from '@aloft/shared-objects';
import {
  Address,
  AloftDate,
  MLSListing,
  PropertyData,
  RecommendedCompsSources,
  SubjectData,
  ToolkitListingID,
  ToolkitOrderID,
  schGeoSearch,
  schListingsFiltersMatchCriteria,
  schRecommendedCompsFilters,
} from '@aloft/toolkit-shared';
import { IconName, ProductColor } from '@aloft/toolkit-sui';
import { SortingState, Table } from '@tanstack/react-table';

import { Dispatch, SetStateAction } from 'react';
import { z } from 'zod';

import { schRecommendCompsForm } from './schemas.ts';

export type CompSelectionProperty = {
  t: 'z' | 'c1' | 'tk';
  id: string;
  address?: Address;
  gla?: AloftArea;
  lotSize?: AloftArea;
  saleDate?: AloftDate;
  contractDate?: AloftDate;
  listingContractDate?: AloftDate;
  salePrice?: AloftPrice;
  listPrice?: AloftPrice;
  distanceToSubject?: number;
  imageUrls?: string[];
  latitude?: number;
  longitude?: number;
  fullBaths?: number;
  halfBaths?: number;
  bedroomCount?: number;
  yearBuilt?: number;
  bedrooms?: number;
  quality?: number;
  condition?: number;
  listingID?: ToolkitListingID;
  similarityScore?: number;
  cumulativeDaysOnMarket?: number;
  basement?: boolean;
  garage?: boolean;
  reo?: boolean;
  shortSale?: boolean;
  mlsStatus?: string;
  garageCount?: number;
  pool?: boolean;
  propertyType?: string;
  qualityTier?: number;
};

export type CompTypeSelection = {
  aiv?: boolean;
  arv?: boolean;
  listingData?: MLSListing;
};

export type CompSelectionState = Record<string, CompTypeSelection>;

type RowSelectionState = {
  rowSelection: CompSelectionState;
  setRowSelection: Dispatch<SetStateAction<CompSelectionState>>;
};

type TableSortingState = {
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
};

export type RecommendedCompSelectionGridProps = RowSelectionState &
  TableSortingState & {
    setFocusedRow: Dispatch<SetStateAction<string | undefined>>;
    data: CompSelectionProperty[];
    subject: CompSelectionProperty;
    comps: CompSelectionProperty[];
    focusedPropertyIndex?: number;
    source: RecommendedCompsSources;
    orderID: string;
    isInspectifyOrder: boolean;
    hasArvOption: boolean;
  };

export type RecommendedCompSelectionDetailsProps = {
  orderID: ToolkitOrderID;
  property?: CompSelectionProperty;
  subject: CompSelectionProperty;
  data: CompSelectionProperty[];
  comps: CompSelectionProperty[];
  compSelectionTableRef: React.MutableRefObject<Table<CompSelectionProperty> | undefined>;
  setFocusedRow: Dispatch<SetStateAction<string | undefined>>;
  sorting: SortingState;
  rowSelection?: CompSelectionState;
  isInspectifyOrder?: boolean;
  hasArvOption?: boolean;
};

export type CraftsmanRecommendedCompSelectionProps = RowSelectionState &
  TableSortingState &
  RecommendedCompSelectionCraftsmanProps;

type RecommendedCompSelectionCraftsmanProps = {
  orderID: ToolkitOrderID;
  subject: SubjectData;
  comps: Record<string, PropertyData>;
  compSeq: string[];
  effectiveDate: AloftDateJSON;
  currentComps: Record<string, string>;
  showNoRecommendations: boolean;
  allowRecommendedCompsFiltering: boolean;
  isInspectifyOrder: boolean;
  hasArvOption: boolean;
  setShowNoRecommendations: Dispatch<SetStateAction<boolean>>;
  getDefaultFormValues: () => InitialCompSearch['body']['search'];
};

export type CompSelectionProps = RowSelectionState &
  TableSortingState & {
    data: CompSelectionProperty[];
    subject: CompSelectionProperty;
    comps: CompSelectionProperty[];
    source: RecommendedCompsSources;
    orderID: string;
    isInspectifyOrder: boolean;
    hasArvOption: boolean;
  };

/// ///////////////////////
// Overall Zod Schema
/// ///////////////////////
export type RecommendCompsFormSchema = typeof schRecommendCompsForm;
export type RecommendCompsFormOutput = z.infer<typeof schRecommendCompsForm>;

export type InitialCompSearch = z.infer<typeof schRecommendedCompsStudy>;
export const schRecommendedCompsStudy = z.object({
  body: z.object({
    search: z.object({
      filters: schRecommendedCompsFilters,
      geoSearch: schGeoSearch,
      matchCriteria: schListingsFiltersMatchCriteria,
    }),
    originalSearch: z.object({
      filters: schRecommendedCompsFilters,
      geoSearch: schGeoSearch,
      matchCriteria: schListingsFiltersMatchCriteria,
    }),
  }),
});

export const schCompSearch = z.object({
  filters: schRecommendedCompsFilters,
  geoSearch: schGeoSearch,
  matchCriteria: schListingsFiltersMatchCriteria,
});
export type CompSearch = z.infer<typeof schCompSearch>;

export type FiltersMessage = {
  type: 'error' | 'warning' | 'success';
  color: ProductColor;
  message: string;
  icon: IconName;
};

export type QualityTierBreakdownData = Record<
  number,
  {
    properties: CompSelectionProperty[];
    priceBand?: string;
    medianGla?: string;
    countOfSales?: number;
  }
>;
